import { createRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button, Card, Field, Input, Screen, Skeleton } from "../../shared/ui/index.js";
import { Shell } from "../../app/Shell.js";
import { requireAuth } from "../../app/guards.js";
import { rootRoute } from "../../app/root-route.js";
import { useToast } from "../../shared/toast.js";
import {
  sendTestEmail,
  useAdminSettings,
  useUpdateSettings,
  type SmtpPatch,
} from "./settings.api.js";

export const adminSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/settings",
  beforeLoad: requireAuth("admin"),
  component: () => (
    <Shell>
      <SettingsScreen />
    </Shell>
  ),
});

function SettingsScreen() {
  const { data, isLoading } = useAdminSettings();
  const update = useUpdateSettings();
  const toast = useToast();

  const [f, setF] = useState<SmtpPatch>({});
  const [pass, setPass] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (data) {
      setF({
        smtpHost: data.smtp.smtpHost,
        smtpPort: data.smtp.smtpPort ?? 587,
        smtpUser: data.smtp.smtpUser,
        smtpSecure: data.smtp.smtpSecure,
        mailFrom: data.smtp.mailFrom,
        mailFromName: data.smtp.mailFromName || "SmartVow",
      });
    }
  }, [data]);

  if (isLoading || !data) {
    return (
      <Screen title="Réglages">
        <Skeleton count={1} className="h-72" />
      </Screen>
    );
  }

  const eff = data.effective;
  const upd = (k: keyof SmtpPatch, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  function save() {
    const patch: SmtpPatch = { ...f };
    if (pass) patch.smtpPass = pass;
    update.mutate(patch, {
      onSuccess: () => {
        setPass("");
        toast("Réglages enregistrés");
      },
      onError: () => toast("Échec de l'enregistrement", "err"),
    });
  }

  async function runTest() {
    setTesting(true);
    try {
      const r = await sendTestEmail(testTo);
      toast(r.sent ? "Email de test envoyé ✓" : `Échec : ${r.error ?? "?"}`, r.sent ? "ok" : "err");
    } catch {
      toast("Échec de l'envoi de test", "err");
    } finally {
      setTesting(false);
    }
  }

  return (
    <Screen title="Réglages">
      <div className="flex flex-col gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-lg text-stone-900">Email (SMTP)</h2>
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${
                eff.configured ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"
              }`}
            >
              {eff.configured ? "configuré" : "non configuré"}
            </span>
          </div>
          <p className="mt-1 text-sm text-stone-500">
            Sert aux emails de bienvenue des invités et aux liens d'invitation des clients. Les
            variables d'environnement du serveur servent de valeurs par défaut.
          </p>

          <div className="mt-4 flex flex-col gap-3">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Field label="Serveur SMTP">
                <Input
                  placeholder="smtp.exemple.fr"
                  value={f.smtpHost ?? ""}
                  onChange={(e) => upd("smtpHost", e.target.value)}
                />
              </Field>
              <Field label="Port">
                <Input
                  type="number"
                  value={f.smtpPort ?? 587}
                  onChange={(e) => upd("smtpPort", Number(e.target.value))}
                />
              </Field>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Field label="Utilisateur">
                <Input
                  autoComplete="off"
                  value={f.smtpUser ?? ""}
                  onChange={(e) => upd("smtpUser", e.target.value)}
                />
              </Field>
              <Field label={`Mot de passe${data.smtp.smtpPassSet ? " (enregistré)" : ""}`}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  placeholder={data.smtp.smtpPassSet ? "•••••••• inchangé" : ""}
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                />
              </Field>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Field label="Expéditeur (from)">
                <Input
                  placeholder="noreply@smartriver.fr"
                  value={f.mailFrom ?? ""}
                  onChange={(e) => upd("mailFrom", e.target.value)}
                />
              </Field>
              <Field label="Nom de l'expéditeur">
                <Input
                  value={f.mailFromName ?? ""}
                  onChange={(e) => upd("mailFromName", e.target.value)}
                />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-600">
              <input
                type="checkbox"
                checked={!!f.smtpSecure}
                onChange={(e) => upd("smtpSecure", e.target.checked)}
              />
              Connexion TLS directe (port 465)
            </label>

            <div>
              <Button onClick={save} disabled={update.isPending}>
                {update.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="font-serif text-lg text-stone-900">Envoyer un email de test</h2>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
            <Field label="Adresse destinataire">
              <Input
                type="email"
                placeholder="vous@exemple.fr"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
            </Field>
            <Button
              variant="ghost"
              onClick={runTest}
              disabled={testing || !testTo || !eff.configured}
            >
              {testing ? "Envoi…" : "Tester"}
            </Button>
          </div>
          {!eff.configured && (
            <p className="mt-2 text-xs text-stone-400">
              Enregistrez d'abord une configuration SMTP valide.
            </p>
          )}
        </Card>
      </div>
    </Screen>
  );
}
